import React, { useState } from 'react';

/**
 * Modal de pendência por imagem não autêntica (cláusulas 3.10/3.10.1):
 * exibido quando POST /registration/complete responde `tacRequired: true`
 * (fluxo legado: suspensão de 40 dias cumprida). Mostra o texto do Termo de
 * Ajuste de Conduta; o aceite é gravado via POST /registration/accept-image-tac
 * e, em seguida, `onAccepted` é chamado para repetir o aceite dos Termos de Uso.
 *
 * O aviso de "multa compensatória pendente" foi removido em 03/10/2026: a
 * cláusula 3.10.1 deixou de instituir cláusula penal (POP-COMP-007 §14.1.7-6).
 * O backend ainda envia `multaCents` por causa dos registros históricos, mas
 * cobrar na tela de retorno seria anunciar ao(à) paciente dívida que a
 * Plataforma não cobra mais. Não reintroduzir.
 *
 * Props:
 *  - open: boolean
 *  - apiBaseUrl: string
 *  - patientId: string
 *  - tacInfo: { message, tacTitle, tacText, tacVersion }
 *  - onAccepted: () => void  (re-tenta o aceite dos termos)
 *  - onBlocked: (message) => void  (backend respondeu bloqueio definitivo)
 */
export default function ImageFraudTacModal({ open, apiBaseUrl, patientId, tacInfo, onAccepted, onBlocked }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open || !tacInfo) return null;

  const handleAcceptTac = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    const url = String(apiBaseUrl || '').replace(/\/+$/, '');
    try {
      const res = await fetch(`${url}/registration/accept-image-tac`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patientId, tacVersion: tacInfo.tacVersion }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.blocked === true) {
        setSubmitting(false);
        if (typeof onBlocked === 'function') onBlocked(data.message || '');
        return;
      }
      if (!res.ok || !data?.success) {
        setSubmitting(false);
        setError(data?.error || 'Não foi possível registrar o aceite. Tente novamente.');
        return;
      }
      setSubmitting(false);
      if (typeof onAccepted === 'function') await onAccepted();
    } catch {
      setSubmitting(false);
      setError('Falha de conexão. Verifique a internet e tente de novo.');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-fraud-tac-title"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10600,
        background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '1rem',
          padding: '1.25rem',
          maxWidth: 460,
          width: '100%',
          maxHeight: 'min(90vh, 640px)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        }}
      >
        <h2
          id="image-fraud-tac-title"
          style={{ margin: '0 0 0.5rem', fontSize: '1.02rem', fontWeight: 700, color: '#B45309' }}
        >
          Pendências para retornar à plataforma
        </h2>
        <p style={{ margin: '0 0 0.6rem', fontSize: '0.82rem', color: '#334155', lineHeight: 1.5 }}>
          {tacInfo.message}
        </p>
        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: 'min(40vh, 260px)',
            overflowY: 'auto',
            border: '1px solid #e2e8f0',
            borderRadius: '0.5rem',
            padding: '0.7rem 0.8rem',
            fontSize: '0.72rem',
            lineHeight: 1.5,
            color: '#334155',
            background: '#f8fafc',
            whiteSpace: 'pre-wrap',
          }}
        >
          {tacInfo.tacText}
        </div>
        {error ? (
          <p role="alert" style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#b91c1c' }}>
            {error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={handleAcceptTac}
          disabled={submitting}
          style={{
            marginTop: '0.75rem',
            width: '100%',
            padding: '0.7rem',
            borderRadius: '0.5rem',
            border: 'none',
            background: submitting ? '#94a3b8' : '#B45309',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.875rem',
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {submitting ? 'Registrando…' : 'Li e aceito o Termo de Ajuste de Conduta'}
        </button>
      </div>
    </div>
  );
}
